/**
 * PIN vault crypto — PIN で SK (Symmetric Key) を wrap / unwrap する。
 *
 * 設計判断 (Option #2、ookey-browser/apps/extension/src/background/pin-vault.ts と parity):
 *
 *   - **KDF**: PBKDF2-SHA256, **600,000 iterations** (Bitwarden parity)。
 *     Argon2id ではなく PBKDF2 を選ぶ理由 — PIN は低エントロピー (4-6 桁 = 10^4-10^6)
 *     のため、KDF を強化しても brute-force 耐性は本質的に限定的。Web Crypto 標準で
 *     ネイティブ対応 (Argon2id は WASM worker 必須) のため軽量化を優先する。
 *   - **Salt**: 16 byte random per PIN (再 setup 時に毎回新規生成)。
 *   - **対称暗号**: AES-256-GCM (12 byte IV / 16 byte tag) — KEY_HIERARCHY.md §8 と統一。
 *   - **AAD**: なし (`pin-vault` という用途固定で十分。recipient binding 不要)。
 *
 * セキュリティ前提:
 *   - PIN vault は **ローカル端末** にのみ保存 (IndexedDB)。サーバには一切送らない。
 *   - 同一端末上での攻撃者 (ブラウザの DevTools / マルウェア) には抗わない設計
 *     (zero-knowledge 維持: SK は wrap 状態でしか永続化されない)。
 *   - 失敗回数は呼出側 (pinVaultService) で監視し、N 回失敗で vault 破棄。
 */

import { aesGcmDecrypt, aesGcmEncrypt } from './aes-gcm'
import { AES_GCM } from './constants'
import { base64ToBytes, bytesToBase64 } from './encoding'
import { randomBytes } from './random'
import type { AesGcmPayload } from './types'
import { zeroize } from './zeroize'

/** PBKDF2 iteration count (Bitwarden parity, 2026 時点)。 */
export const PIN_PBKDF2_ITERATIONS = 600_000

/** PIN 派生鍵 salt の byte 長。 */
export const PIN_SALT_BYTES = 16

/** AES-256-GCM 鍵長 (32 byte = 256 bit)。 */
const DERIVED_KEY_BITS = AES_GCM.KEY_BYTES * 8

/** PIN 形式の許容範囲 (numeric 4-6 桁)。 */
export const PIN_MIN_LENGTH = 4
export const PIN_MAX_LENGTH = 6
const PIN_PATTERN = /^[0-9]{4,6}$/

export const isValidPin = (pin: string): boolean => PIN_PATTERN.test(pin)

/**
 * IndexedDB に保存される PIN vault の永続化形式 (base64)。
 *
 * ookey-browser の StoredPinVault と field 互換 (chrome.storage.local ↔ IndexedDB の
 * 媒体差以外は完全一致)。version で将来の format migration に対応。
 */
export interface StoredPinVault {
  readonly version: 1
  readonly salt: string
  readonly iv: string
  readonly ciphertext: string
  readonly authTag: string
  readonly iterations: number
  readonly failedAttempts: number
  /** 設定時の PIN 桁数 (4-6)。LockPage の入力 box 数をこの値に合わせる。 */
  readonly pinLength: number
  /** 設定時のユーザーメール (正規化済 lowercase)。読み取り時に現セッションの
   * material.email と照合し、不一致なら別アカウントの残骸として vault を破棄する
   * (multi-account 切替時の前ユーザー SK leak 防止、TASK-WEB-PIN review I-1)。 */
  readonly userEmail: string
}

/**
 * PIN から PBKDF2 で 256-bit 鍵を派生する。
 *
 * derive 後 pinBytes を即 zeroize する (JavaScript string PIN 自体は immutable のため
 * 完全な消去は不能 — 呼出側でも PIN を変数に長期保持しないこと)。
 */
const derivePinKey = async (
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array> => {
  const encoder = new TextEncoder()
  const pinBytes = encoder.encode(pin)

  try {
    const baseKey = await globalThis.crypto.subtle.importKey(
      'raw',
      pinBytes as Uint8Array<ArrayBuffer>,
      'PBKDF2',
      false,
      ['deriveBits'],
    )

    const derived = await globalThis.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt as Uint8Array<ArrayBuffer>,
        iterations,
        hash: 'SHA-256',
      },
      baseKey,
      DERIVED_KEY_BITS,
    )

    return new Uint8Array(derived)
  } finally {
    zeroize(pinBytes)
  }
}

/**
 * SK を PIN で wrap し、永続化に必要な全 field を含む StoredPinVault を返す。
 *
 * @param pin           4-6 桁の数値 PIN
 * @param symmetricKey  32 byte の SK (sessionStore から取得)
 * @returns StoredPinVault (failedAttempts = 0 で初期化)
 */
export const sealSkWithPin = async (
  pin: string,
  symmetricKey: Uint8Array,
  userEmail: string,
): Promise<StoredPinVault> => {
  if (!isValidPin(pin)) {
    throw new RangeError(`PIN must be ${PIN_MIN_LENGTH}-${PIN_MAX_LENGTH} numeric digits`)
  }
  if (symmetricKey.length !== AES_GCM.KEY_BYTES) {
    throw new RangeError(`symmetricKey must be ${AES_GCM.KEY_BYTES} bytes`)
  }
  if (userEmail === '') {
    throw new RangeError('userEmail required for PIN vault binding')
  }

  const salt = randomBytes(PIN_SALT_BYTES)
  const iv = randomBytes(AES_GCM.IV_BYTES)
  const pinKey = await derivePinKey(pin, salt, PIN_PBKDF2_ITERATIONS)

  try {
    const payload = await aesGcmEncrypt(pinKey, iv, symmetricKey)

    return {
      version: 1,
      salt: bytesToBase64(salt),
      iv: payload.iv,
      ciphertext: payload.ciphertext,
      authTag: payload.authTag,
      iterations: PIN_PBKDF2_ITERATIONS,
      failedAttempts: 0,
      pinLength: pin.length,
      userEmail: userEmail.toLowerCase(),
    }
  } finally {
    zeroize(pinKey)
  }
}

/**
 * PIN と StoredPinVault から SK を復元する。
 *
 * PIN 違い / vault 改ざんは Web Crypto が `OperationError` を throw する
 * (aes-gcm.ts はラップしないため). 呼出側 (pinVaultService) で catch して
 * failedAttempts を increment する想定。
 */
export const openSkWithPin = async (pin: string, vault: StoredPinVault): Promise<Uint8Array> => {
  if (!isValidPin(pin)) {
    throw new RangeError(`PIN must be ${PIN_MIN_LENGTH}-${PIN_MAX_LENGTH} numeric digits`)
  }

  const salt = base64ToBytes(vault.salt)
  const pinKey = await derivePinKey(pin, salt, vault.iterations)

  try {
    const payload: AesGcmPayload = {
      iv: vault.iv,
      ciphertext: vault.ciphertext,
      authTag: vault.authTag,
    }

    return await aesGcmDecrypt(pinKey, payload)
  } finally {
    zeroize(pinKey)
  }
}
